import { BRANCH_NAMES, SEVERITY_VARIANTS } from "@consts";
import { useEffect, useState } from "react";
import { queryClient } from "src/lib/react-query/queryClient";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { useDraft } from "src/queries/hooks/assistants";
import { useMutateExtendPreview, usePreviewBranchDeployer } from "src/queries/hooks/preview";
import useAppStore from "src/stores/appStore";
import { assistantIdSelector, selectedAccountIdSelector, systemModeSelector } from "src/stores/selectors/appSelectors";
import { SYSTEM_MODES } from "src/stores/slices/createAppSlice";
import { ERROR_EXTENDING_PREVIEW_MESSAGE, EXTENDED_SUCCESS_MESSAGE, ONE_DAY_MS, ONE_HOUR_MS, PREVIEW_EXTEND_KEY } from "./ExtendPreviewBanner.consts";

const useExtendPreview = (addSnackbarMessage, setSnackbarSeverity) => {
  const [previewExpiration, setPreviewExpiration] = useState({ days: 0, hours: 0 });
  const { data: draft } = useDraft();
  const assistantId = useAppStore(assistantIdSelector);
  const accountId = useAppStore(selectedAccountIdSelector);
  const systemMode = useAppStore(systemModeSelector);
  const branchName = systemMode === SYSTEM_MODES.EDIT ? draft?.branchName : BRANCH_NAMES.MASTER;

  const { data: previewBranchDeployer, isLoading: isLoadingPreviewBranchDeployer, refetch: refetchPreviewBranchDeployer } = usePreviewBranchDeployer({
    accountId,
    assistantId,
    branchName
  });

  const { mutateAsync: extendPreview, isPending: isExtendingPreview } = useMutateExtendPreview();

  useEffect(() => {
    if (previewBranchDeployer) {
      const { expiresAt, updatedAt } = previewBranchDeployer;
      if (!expiresAt) return;
      const expiration = new Date(expiresAt).getTime();

      let daysUntilExpiration = 0;
      let hoursUntilExpiration = 0;

      if (!isNaN(expiration)) {
        const now = Date.now();
        const timeRemaining = expiration - now;

        if (timeRemaining > 0) {
          daysUntilExpiration = Math.floor(timeRemaining / ONE_DAY_MS);
          hoursUntilExpiration = Math.floor((timeRemaining % ONE_DAY_MS) / ONE_HOUR_MS);
        }
      }

      setPreviewExpiration({ days: daysUntilExpiration, hours: hoursUntilExpiration });
    }
  }, [previewBranchDeployer]);

  const getExpirationText = () => {
    if (previewExpiration.days === 0 && previewExpiration.hours === 0) return "Less than 1 hour";
    if (previewExpiration.days === 0) return `${previewExpiration.hours} ${previewExpiration.hours === 1 ? "hour" : "hours"}`;
    return `${previewExpiration.days} ${previewExpiration.days === 1 ? "day" : "days"}, ${previewExpiration.hours} ${previewExpiration.hours === 1 ? "hour" : "hours"}`;
  };

  const handleExtendPreview = async () => {
    const storageKey = `${assistantId}:${PREVIEW_EXTEND_KEY}`;
    const lastExtendTimestamp = localStorage.getItem(storageKey);
    const now = Date.now();

    if (lastExtendTimestamp) {
      const timeSinceLastExtend = now - parseInt(lastExtendTimestamp, 10);

      if (timeSinceLastExtend < ONE_DAY_MS) {
        addSnackbarMessage(EXTENDED_SUCCESS_MESSAGE);
        return;
      }
    }

    try {
      await extendPreview({ assistantId });
      localStorage.setItem(storageKey, now.toString());
      addSnackbarMessage(EXTENDED_SUCCESS_MESSAGE);
      queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.NOTIFICATIONS] });
      queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.PREVIEW_BRANCH_DEPLOYER] });
      await refetchPreviewBranchDeployer();
    } catch {
      setSnackbarSeverity(SEVERITY_VARIANTS.ERROR);
      addSnackbarMessage(ERROR_EXTENDING_PREVIEW_MESSAGE);
    }
  };

  return {
    isExtendingPreview,
    handleExtendPreview,
    previewBranchDeployer,
    isLoadingPreviewBranchDeployer,
    getExpirationText
  };
};

export default useExtendPreview;
